/**
 * FishingMinigameUI - Stardew Valley-style Bobber Minigame
 * 
 * A skill-based minigame that increases catch quality when played well.
 * The player must keep a bobber within a moving target zone by pressing SPACE.
 * 
 * - Press SPACE to make the bobber rise
 * - Release to let it fall (gravity)
 * - Keep the bobber inside the green "catch zone" to fill the progress bar
 * - Higher completion = better catch quality bonus
 * 
 * This is OPTIONAL - players can fish without engaging the minigame,
 * but playing well increases catch speed and rarity chances.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';

// ============ INTERFACES ============

export interface FishingMinigameState {
  isActive: boolean;
  bobberY: number;           // Bobber position (0-1, 0 = top, 1 = bottom)
  bobberVelocity: number;    // Current velocity
  catchZoneY: number;        // Catch zone center position (0-1)
  catchZoneSize: number;     // Catch zone height (0-1)
  catchZoneVelocity: number; // Catch zone movement speed
  progressMeter: number;     // 0-100, how much progress toward catch
  difficultyLevel: number;   // 1-5, affects zone size and speed
  isSpacePressed: boolean;   // Whether space is currently held
  spotType: string;          // Current fishing spot type
  spotId: string;            // Current fishing spot ID
}

export interface FishingMinigameCallbacks {
  onCatchComplete: (quality: number) => void;  // 0-100 quality score
  onCatchFailed: () => void;
  onDeactivate: () => void;
}

// ============ CONSTANTS ============

const BAR_WIDTH = 40;
const BAR_HEIGHT = 200;
const BOBBER_SIZE = 16;
const CATCH_ZONE_MIN_SIZE = 0.15;  // Minimum catch zone (hard fish)
const CATCH_ZONE_MAX_SIZE = 0.35;  // Maximum catch zone (easy fish)

// Physics - Smooth, predictable movement
const GRAVITY = 0.0025;          // Gravity pulling bobber down (reduced for smoother feel)
const LIFT_FORCE = 0.008;        // Force when pressing space (reduced for control)
const MAX_VELOCITY = 0.018;      // Maximum bobber velocity (reduced for smoothness)
const BOUNCE_DAMPING = 0.4;      // Velocity reduction on bounce (more damping = less bouncy)
const VELOCITY_SMOOTHING = 0.15; // Smooth velocity changes over time (new)

// Progress
const PROGRESS_GAIN_RATE = 1.4;  // Progress gain when in zone (slightly faster)
const PROGRESS_DECAY_RATE = 0.6; // Progress loss when out of zone (slower decay)
const CATCH_THRESHOLD = 100;     // Progress needed to catch

// Catch zone movement - predictable, smooth drifting
const ZONE_SPEED_MIN = 0.0012;   // Minimum zone drift speed
const ZONE_SPEED_MAX = 0.004;    // Maximum zone drift speed
const ZONE_DIRECTION_CHANGE_CHANCE = 0.008; // Less frequent direction changes
const ZONE_ACCELERATION = 0.00015; // Smooth speed changes instead of sudden jumps

// Difficulty settings by spot type
const SPOT_DIFFICULTY: Record<string, number> = {
  shallow_waters: 1,
  deep_sea: 2,
  coral_reef: 3,
  fishing_hole: 4,
};

// ============ FISHING MINIGAME UI SYSTEM ============

export class FishingMinigameUI {
  private scene: Phaser.Scene;
  private socket: Socket | null = null;
  private callbacks: FishingMinigameCallbacks;
  
  // UI Elements
  private container: Phaser.GameObjects.Container | null = null;
  private barBackground: Phaser.GameObjects.Rectangle | null = null;
  private catchZone: Phaser.GameObjects.Rectangle | null = null;
  private bobber: Phaser.GameObjects.Graphics | null = null;
  private progressBarBg: Phaser.GameObjects.Rectangle | null = null;
  private progressBarFill: Phaser.GameObjects.Rectangle | null = null;
  private instructionText: Phaser.GameObjects.Text | null = null;
  private qualityText: Phaser.GameObjects.Text | null = null;
  
  // Fishing prompt (shown when fishing but minigame not active)
  private fishingPrompt: Phaser.GameObjects.Container | null = null;
  private isFishing: boolean = false;
  
  // State
  private state: FishingMinigameState = {
    isActive: false,
    bobberY: 0.5,
    bobberVelocity: 0,
    catchZoneY: 0.5,
    catchZoneSize: 0.25,
    catchZoneVelocity: ZONE_SPEED_MIN,
    progressMeter: 25,  // Start with some progress
    difficultyLevel: 1,
    isSpacePressed: false,
    spotType: 'shallow_waters',
    spotId: '',
  };
  
  // Input
  private spaceKey: Phaser.Input.Keyboard.Key | null = null;

  constructor(scene: Phaser.Scene, callbacks: FishingMinigameCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  // ============ PUBLIC API ============

  public setSocket(socket: Socket | null): void {
    this.socket = socket;
  }

  public isActive(): boolean {
    return this.state.isActive;
  }

  public getProgress(): number {
    return this.state.progressMeter;
  }

  /**
   * Show/hide fishing prompt based on whether player is fishing
   * NOTE: Does NOT deactivate active minigame - minigame should only be deactivated by player action
   */
  public setIsFishing(isFishing: boolean, spotType?: string, spotId?: string): void {
    this.isFishing = isFishing;
    
    if (isFishing) {
      // Update spot info
      this.state.spotType = spotType || 'shallow_waters';
      this.state.spotId = spotId || '';
      
      // If minigame is not active, show the prompt
      // If minigame IS active, keep it active (don't touch prompt)
      if (!this.state.isActive) {
        this.showFishingPrompt();
      }
    } else {
      // Fishing stopped - hide prompt only
      // Do NOT deactivate active minigame - let player close it or let it complete naturally
      this.hideFishingPrompt();
    }
  }

  public activate(spotType?: string, spotId?: string): void {
    // Clean up any existing UI first (in case of stale state)
    this.destroyUI();
    
    // Hide fishing prompt when activating
    this.hideFishingPrompt();
    
    // Set difficulty based on spot type
    const difficulty = SPOT_DIFFICULTY[spotType || this.state.spotType] || 1;
    
    // Calculate catch zone size (smaller = harder)
    const zoneSize = CATCH_ZONE_MAX_SIZE - (difficulty - 1) * 0.05;
    
    // Calculate catch zone speed (faster = harder)
    const zoneSpeed = ZONE_SPEED_MIN + (difficulty - 1) * 0.001;
    
    this.state = {
      isActive: true,
      bobberY: 0.5,
      bobberVelocity: 0,
      catchZoneY: 0.3 + Math.random() * 0.4,  // Random start position
      catchZoneSize: Math.max(CATCH_ZONE_MIN_SIZE, zoneSize),
      catchZoneVelocity: zoneSpeed * (Math.random() > 0.5 ? 1 : -1),
      progressMeter: 25,  // Start with some progress
      difficultyLevel: difficulty,
      isSpacePressed: false,
      spotType: spotType || this.state.spotType,
      spotId: spotId || this.state.spotId,
    };
    
    this.createUI();
    this.setupInput();
  }

  public deactivate(): void {
    if (!this.state.isActive) return;
    
    this.state.isActive = false;
    this.destroyUI();
    this.callbacks.onDeactivate();
    
    // Show fishing prompt again if still fishing
    if (this.isFishing) {
      this.showFishingPrompt();
    }
  }

  public update(delta: number): void {
    // Update fishing prompt visibility
    if (this.isFishing && !this.state.isActive && !this.fishingPrompt) {
      this.showFishingPrompt();
    } else if (!this.isFishing && this.fishingPrompt) {
      this.hideFishingPrompt();
    }
    
    if (!this.state.isActive) return;
    
    // Update bobber physics
    this.updateBobber(delta);
    
    // Update catch zone movement
    this.updateCatchZone(delta);
    
    // Check if bobber is in catch zone
    this.updateProgress(delta);
    
    // Check for catch completion or failure
    this.checkCatchStatus();
    
    // Update visuals
    this.updateVisuals();
  }

  // ============ PRIVATE METHODS ============

  private promptTween: Phaser.Tweens.Tween | null = null;
  
  private showFishingPrompt(): void {
    // Always clean up first to avoid stale state
    this.hideFishingPrompt();
    
    const { width, height } = this.scene.cameras.main;
    
    this.fishingPrompt = this.scene.add.container(width / 2, height - 100);
    this.fishingPrompt.setScrollFactor(0);
    this.fishingPrompt.setDepth(1000); // Increased depth to ensure visibility
    this.fishingPrompt.setVisible(true);
    
    // Background
    const bg = this.scene.add.rectangle(0, 0, 300, 50, 0x000000, 0.85);
    bg.setStrokeStyle(2, 0x5dade2);
    this.fishingPrompt.add(bg);
    
    // Text
    const text = this.scene.add.text(0, -8, 'Press [SPACE] for Focus Fishing', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '14px',
      color: '#5dade2',
    });
    text.setOrigin(0.5);
    this.fishingPrompt.add(text);
    
    // Subtext
    const subtext = this.scene.add.text(0, 12, 'Catch faster with better quality!', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#888888',
    });
    subtext.setOrigin(0.5);
    this.fishingPrompt.add(subtext);
    
    // Pulse animation - store reference so we can stop it
    this.promptTween = this.scene.tweens.add({
      targets: bg,
      alpha: 0.6,
      duration: 800,
      yoyo: true,
      repeat: -1,
    });
    
    console.log(`🎣 Fishing prompt shown at (${width / 2}, ${height - 100}), depth: 1000, isFishing: ${this.isFishing}`);
  }

  private hideFishingPrompt(): void {
    // Stop tween first to prevent it from running on destroyed objects
    if (this.promptTween) {
      this.promptTween.stop();
      this.promptTween = null;
    }
    
    if (this.fishingPrompt) {
      this.fishingPrompt.destroy();
      this.fishingPrompt = null;
      console.log('🎣 Fishing prompt hidden');
    }
  }

  private createUI(): void {
    const { width, height } = this.scene.cameras.main;
    
    // Position on right side of screen
    const panelWidth = 100;
    const panelHeight = BAR_HEIGHT + 100;
    const x = width - panelWidth / 2 - 30;
    const y = height / 2;
    
    this.container = this.scene.add.container(x, y);
    this.container.setScrollFactor(0);
    this.container.setDepth(900);
    
    // Background panel
    const panelBg = this.scene.add.rectangle(0, 0, panelWidth, panelHeight, 0x0a0a0a, 0.95);
    panelBg.setStrokeStyle(2, 0x3a3a3a);
    this.container.add(panelBg);
    
    // Click blocker
    const clickBlocker = this.scene.add.rectangle(0, 0, panelWidth, panelHeight, 0x000000, 0);
    clickBlocker.setInteractive({ useHandCursor: false });
    clickBlocker.on('pointerdown', (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
    });
    this.container.add(clickBlocker);
    
    // Title bar
    const titleBar = this.scene.add.rectangle(0, -panelHeight / 2 + 15, panelWidth - 4, 26, 0x1a1a2e, 1);
    this.container.add(titleBar);
    
    const title = this.scene.add.text(0, -panelHeight / 2 + 15, 'FISHING', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: '#5dade2',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.container.add(title);
    
    // Main fishing bar background
    this.barBackground = this.scene.add.rectangle(0, 0, BAR_WIDTH, BAR_HEIGHT, 0x0d1117);
    this.barBackground.setStrokeStyle(2, 0x30363d);
    this.container.add(this.barBackground);
    
    // Catch zone (green area)
    const zoneHeight = this.state.catchZoneSize * BAR_HEIGHT;
    const zoneY = (this.state.catchZoneY - 0.5) * BAR_HEIGHT;
    this.catchZone = this.scene.add.rectangle(0, zoneY, BAR_WIDTH - 4, zoneHeight, 0x22c55e, 0.4);
    this.catchZone.setStrokeStyle(2, 0x22c55e);
    this.container.add(this.catchZone);
    
    // Bobber (player-controlled)
    this.bobber = this.scene.add.graphics();
    this.drawBobber(0x5dade2);
    this.container.add(this.bobber);
    
    // Progress bar background
    const progressY = BAR_HEIGHT / 2 + 25;
    this.progressBarBg = this.scene.add.rectangle(0, progressY, BAR_WIDTH, 10, 0x21262d);
    this.progressBarBg.setStrokeStyle(1, 0x30363d);
    this.container.add(this.progressBarBg);
    
    // Progress bar fill
    this.progressBarFill = this.scene.add.rectangle(
      -BAR_WIDTH / 2, progressY, 0, 8, 0x5dade2
    );
    this.progressBarFill.setOrigin(0, 0.5);
    this.container.add(this.progressBarFill);
    
    // Quality text
    this.qualityText = this.scene.add.text(0, progressY + 18, 'CATCH: 25%', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#5dade2',
      fontStyle: 'bold',
    });
    this.qualityText.setOrigin(0.5);
    this.container.add(this.qualityText);
    
    // Instruction text
    this.instructionText = this.scene.add.text(0, progressY + 35, '[SPACE]', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#8b949e',
    });
    this.instructionText.setOrigin(0.5);
    this.container.add(this.instructionText);
  }

  private drawBobber(color: number): void {
    if (!this.bobber) return;
    
    this.bobber.clear();
    
    // Draw bobber as a small square with highlight
    this.bobber.fillStyle(color, 1);
    this.bobber.fillRect(-BOBBER_SIZE / 2, -BOBBER_SIZE / 2, BOBBER_SIZE, BOBBER_SIZE);
    
    // Border
    this.bobber.lineStyle(2, 0xffffff, 0.8);
    this.bobber.strokeRect(-BOBBER_SIZE / 2, -BOBBER_SIZE / 2, BOBBER_SIZE, BOBBER_SIZE);
    
    // Center dot
    this.bobber.fillStyle(0xffffff, 0.9);
    this.bobber.fillRect(-2, -2, 4, 4);
  }

  private setupInput(): void {
    if (!this.scene.input.keyboard) return;
    
    // Get reference to the existing SPACE key (managed by InputHandler)
    // Don't create a new one - just get the existing key object
    const keys = this.scene.input.keyboard.keys;
    this.spaceKey = keys[Phaser.Input.Keyboard.KeyCodes.SPACE] || null;
    
    // If no existing key, create one (shouldn't happen normally)
    if (!this.spaceKey) {
      this.spaceKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE, false, false);
    }
  }

  private destroyUI(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    
    // Just null our reference - don't remove the key since InputHandler owns it
    this.spaceKey = null;
  }

  private updateBobber(delta: number): void {
    // Check if space is pressed
    this.state.isSpacePressed = this.spaceKey?.isDown || false;
    
    // Calculate target velocity based on input
    let targetVelocity = this.state.bobberVelocity;
    if (this.state.isSpacePressed) {
      // Lift force when pressing space
      targetVelocity -= LIFT_FORCE;
    } else {
      // Gravity when not pressing
      targetVelocity += GRAVITY;
    }
    
    // Smooth velocity changes for less twitchy movement
    this.state.bobberVelocity += (targetVelocity - this.state.bobberVelocity) * VELOCITY_SMOOTHING;
    
    // Clamp velocity
    this.state.bobberVelocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, this.state.bobberVelocity));
    
    // Apply velocity
    this.state.bobberY += this.state.bobberVelocity;
    
    // Bounce off edges with smooth damping
    if (this.state.bobberY <= 0) {
      this.state.bobberY = 0;
      // Reduce velocity smoothly instead of instant reversal
      this.state.bobberVelocity = Math.max(0, this.state.bobberVelocity * BOUNCE_DAMPING);
    } else if (this.state.bobberY >= 1) {
      this.state.bobberY = 1;
      // Reduce velocity smoothly instead of instant reversal
      this.state.bobberVelocity = Math.min(0, this.state.bobberVelocity * BOUNCE_DAMPING);
    }
  }

  private updateCatchZone(delta: number): void {
    // Move catch zone
    this.state.catchZoneY += this.state.catchZoneVelocity;
    
    // Calculate bounds (zone should stay within bar)
    const halfZone = this.state.catchZoneSize / 2;
    const minY = halfZone;
    const maxY = 1 - halfZone;
    
    // Smooth bounce off edges (reverse direction gradually)
    if (this.state.catchZoneY <= minY) {
      this.state.catchZoneY = minY;
      // Reverse direction smoothly
      if (this.state.catchZoneVelocity < 0) {
        this.state.catchZoneVelocity = Math.abs(this.state.catchZoneVelocity);
      }
    } else if (this.state.catchZoneY >= maxY) {
      this.state.catchZoneY = maxY;
      // Reverse direction smoothly
      if (this.state.catchZoneVelocity > 0) {
        this.state.catchZoneVelocity = -Math.abs(this.state.catchZoneVelocity);
      }
    }
    
    // Occasionally change direction for unpredictability (less frequently)
    if (Math.random() < ZONE_DIRECTION_CHANGE_CHANCE) {
      this.state.catchZoneVelocity *= -1;
    }
    
    // Smooth speed variation instead of sudden changes
    if (Math.random() < 0.005) {
      const baseSpeed = ZONE_SPEED_MIN + (this.state.difficultyLevel - 1) * 0.0008;
      const currentSpeed = Math.abs(this.state.catchZoneVelocity);
      // Gradually accelerate/decelerate toward a new target speed
      const targetSpeed = baseSpeed + (Math.random() - 0.5) * 0.0015;
      const newSpeed = currentSpeed + (targetSpeed - currentSpeed) * ZONE_ACCELERATION;
      const clampedSpeed = Math.max(ZONE_SPEED_MIN, Math.min(ZONE_SPEED_MAX, newSpeed));
      this.state.catchZoneVelocity = clampedSpeed * Math.sign(this.state.catchZoneVelocity);
    }
  }

  private updateProgress(delta: number): void {
    // Check if bobber is within catch zone
    const halfZone = this.state.catchZoneSize / 2;
    const zoneTop = this.state.catchZoneY - halfZone;
    const zoneBottom = this.state.catchZoneY + halfZone;
    
    const isInZone = this.state.bobberY >= zoneTop && this.state.bobberY <= zoneBottom;
    
    if (isInZone) {
      // Gain progress - faster when closer to center
      const distFromCenter = Math.abs(this.state.bobberY - this.state.catchZoneY);
      const closeness = 1 - (distFromCenter / halfZone);
      // Bonus for being very close to center
      const bonus = closeness > 0.8 ? 1.3 : 1.0;
      this.state.progressMeter = Math.min(CATCH_THRESHOLD, this.state.progressMeter + PROGRESS_GAIN_RATE * bonus * (1 + closeness * 0.3));
    } else {
      // Lose progress more gradually when out of zone
      this.state.progressMeter = Math.max(0, this.state.progressMeter - PROGRESS_DECAY_RATE);
    }
  }

  private checkCatchStatus(): void {
    // Check for successful catch
    if (this.state.progressMeter >= CATCH_THRESHOLD) {
      // Calculate quality based on how well they played
      // Quality affects catch rarity bonus
      const quality = Math.min(100, this.state.progressMeter);
      
      // Send catch complete and then deactivate.
      // Keeping it open allowed repeated rapid catches which felt too fast.
      this.callbacks.onCatchComplete(quality);

      this.deactivate();
      return;
    }
    
    // DON'T auto-fail when progress hits 0 - fishing isn't like combat
    // The player can keep trying as long as the fishing spot exists
    // Progress just stays at 0 until they get the bobber back in the zone
  }

  private updateVisuals(): void {
    if (!this.container) return;
    
    // Check if bobber is in zone for color
    const halfZone = this.state.catchZoneSize / 2;
    const zoneTop = this.state.catchZoneY - halfZone;
    const zoneBottom = this.state.catchZoneY + halfZone;
    const isInZone = this.state.bobberY >= zoneTop && this.state.bobberY <= zoneBottom;
    
    // Update catch zone position
    if (this.catchZone) {
      const zoneHeight = this.state.catchZoneSize * BAR_HEIGHT;
      const zoneY = (this.state.catchZoneY - 0.5) * BAR_HEIGHT;
      this.catchZone.setPosition(0, zoneY);
      this.catchZone.setSize(BAR_WIDTH - 4, zoneHeight);
    }
    
    // Update bobber position and color
    if (this.bobber) {
      const bobberY = (this.state.bobberY - 0.5) * BAR_HEIGHT;
      this.bobber.setPosition(0, bobberY);
      
      // Change color based on whether in zone
      if (isInZone) {
        this.drawBobber(0x22c55e);  // Green when in zone
      } else {
        this.drawBobber(0x5dade2);  // Blue when out of zone
      }
    }
    
    // Update progress bar
    if (this.progressBarFill) {
      const fillWidth = (this.state.progressMeter / CATCH_THRESHOLD) * BAR_WIDTH;
      this.progressBarFill.setSize(fillWidth, 8);
      
      // Color based on progress
      if (this.state.progressMeter >= 80) {
        this.progressBarFill.setFillStyle(0x22c55e);  // Green
      } else if (this.state.progressMeter >= 50) {
        this.progressBarFill.setFillStyle(0xf6e05e);  // Yellow
      } else if (this.state.progressMeter >= 25) {
        this.progressBarFill.setFillStyle(0x5dade2);  // Blue
      } else {
        this.progressBarFill.setFillStyle(0xfc8181);  // Red
      }
    }
    
    // Update quality text
    if (this.qualityText) {
      this.qualityText.setText(`CATCH: ${Math.round(this.state.progressMeter)}%`);
      
      // Color based on progress
      if (this.state.progressMeter >= 80) {
        this.qualityText.setColor('#22c55e');
      } else if (this.state.progressMeter >= 50) {
        this.qualityText.setColor('#f6e05e');
      } else if (this.state.progressMeter >= 25) {
        this.qualityText.setColor('#5dade2');
      } else {
        this.qualityText.setColor('#fc8181');
      }
    }
    
    // Update instruction text based on state
    if (this.instructionText) {
      if (this.state.isSpacePressed) {
        this.instructionText.setText('[SPACE] ^');
        this.instructionText.setColor('#5dade2');
      } else {
        this.instructionText.setText('[SPACE]');
        this.instructionText.setColor('#8b949e');
      }
    }
  }
}
